import React, { useState, useEffect } from "react";
import { Form, Input, Button, Checkbox, message } from "antd";
import { useNavigate } from "react-router-dom";
import { login, sendOtp, verifyOtp, resetPasswordWithOtp, getProfile } from "../../utils/API";
import {
  seedOrderTrackingCache,
  flushOrderTrackingDirty,
} from "../../utils/orderTrackingCache";

type ViewMode = "login" | "forgotEmail" | "forgotOtp" | "forgotReset";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [view, setView] = useState<ViewMode>("login");
  const [forgotEmail, setForgotEmail] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [loginForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [otpForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const Logo_Main = require("../../Assets/logo.png");
  const Login_Bg = require("../../Assets/login.png");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const lastQuoteId = localStorage.getItem("lastOpenedQuoteId");
      if (lastQuoteId) {
        navigate(`/Quotes?quoteId=${encodeURIComponent(lastQuoteId)}`, { replace: true });
      } else {
        navigate("/Quotes", { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const onFinishLogin = async (values: any) => {
    setLoading(true);
    setLoginError("");
    try {
      const response = await login(values);

      if (response && response.data) {
        if (response.data.success) {
          if (response.data.token) {
            localStorage.setItem("token", response.data.token);
          }
          if (response.data.user) {
            localStorage.setItem("user", JSON.stringify(response.data.user));
          } else {
            // Login response only returns a token/userId, not the user object —
            // fetch it separately so the logged-in user's email is available client-side.
            try {
              const profileRes = await getProfile();
              if (profileRes?.data?.success && profileRes.data.user) {
                localStorage.setItem("user", JSON.stringify(profileRes.data.user));
              }
            } catch { /* non-fatal: email-gated features will just fall back to default behavior */ }
          }
          // Warm the Order Tracking cache + retry any pending saves in the
          // background — don't block navigation on it.
          flushOrderTrackingDirty().finally(() => {
            seedOrderTrackingCache();
          });
          const lastQuoteId = localStorage.getItem("lastOpenedQuoteId");
          if (lastQuoteId) {
            navigate(`/Quotes?quoteId=${encodeURIComponent(lastQuoteId)}`);
          } else {
            navigate("/Quotes");
          }
        } else {
          setLoginError(response.data.message || "Invalid email or password");
        }
      } else {
        setLoginError("Unexpected response from server");
      }
    } catch (error: any) {
      if (error.response) {
        if (error.response.data && error.response.data.message) {
          setLoginError(error.response.data.message);
        } else if (error.response.status === 401) {
          setLoginError("Invalid email or password");
        } else if (error.response.status === 400) {
          setLoginError("Bad request. Please check your input.");
        } else if (error.response.status === 403) {
          setLoginError("Access denied. Please contact support.");
        } else if (error.response.status === 500) {
          setLoginError("Server error. Please try again later.");
        } else {
          setLoginError("Login failed. Please try again.");
        }
      } else if (error.request) {
        setLoginError("Network error. Please check your connection.");
      } else {
        setLoginError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Send OTP to email
  const onSendOtp = async (values: { email: string }) => {
    setLoading(true);
    try {
      const response = await sendOtp({ email: values.email });
      if (response?.data?.success) {
        // message.success(response.data.message || "OTP sent to your email");
        setForgotEmail(values.email);
        setView("forgotOtp");
        setCountdown(60);
      } else {
        message.error(response?.data?.message || "Failed to send OTP");
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message || "Failed to send OTP. Please try again.";
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const onVerifyOtp = async (values: { otp: string }) => {
    setLoading(true);
    try {
      const response = await verifyOtp({ email: forgotEmail, otp: values.otp });
      if (response?.data?.success) {
        // message.success("OTP verified successfully");
        setOtpValue(values.otp);
        setView("forgotReset");
      } else {
        message.error(response?.data?.message || "Invalid OTP");
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message || "OTP verification failed. Please try again.";
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset password
  const onResetPassword = async (values: { newPassword: string }) => {
    setLoading(true);
    try {
      const response = await resetPasswordWithOtp({
        email: forgotEmail,
        otp: otpValue,
        newPassword: values.newPassword,
      });
      if (response?.data?.success) {
        // message.success("Password reset successfully! Please login.");
        setView("login");
        setForgotEmail("");
        setOtpValue("");
        resetForm.resetFields();
        otpForm.resetFields();
        emailForm.resetFields();
      } else {
        message.error(response?.data?.message || "Failed to reset password");
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message || "Password reset failed. Please try again.";
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setLoading(true);
    try {
      const response = await sendOtp({ email: forgotEmail });
      if (response?.data?.success) {
        // message.success("OTP resent to your email");
        setCountdown(60);
      } else {
        message.error(response?.data?.message || "Failed to resend OTP");
      }
    } catch (error: any) {
      message.error("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const goBackToLogin = () => {
    setView("login");
    setForgotEmail("");
    setOtpValue("");
    setCountdown(0);
  };

  const renderTitle = () => {
    switch (view) {
      case "login":
        return "Welcome Back";
      case "forgotEmail":
        return "Forgot Password";
      case "forgotOtp":
        return "Verify OTP";
      case "forgotReset":
        return "Reset Password";
    }
  };

  const renderSubtitle = () => {
    switch (view) {
      case "forgotEmail":
        return "Enter your email to receive a verification code";
      case "forgotOtp":
        return (
          <>
            Enter the 6-digit code sent to{" "}
            <span className="font-medium text-gray-800">{forgotEmail}</span>
          </>
        );
      case "forgotReset":
        return "Create a new password for your account";
      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen w-full bg-white overflow-hidden">
      {/* Left Side: Decorative Image */}
      <div className="hidden lg:block lg:w-1/2 h-full relative">
        <img
          src={Login_Bg}
          alt="Laboratory Equipment"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/10"></div>
      </div>

      {/* Right Side: Form */}
      <div className="w-full lg:w-1/2 h-full overflow-y-auto">
        <div className="min-h-full flex flex-col justify-center items-center px-4 sm:px-8 lg:px-12 py-8">
          <div className="max-w-md w-full">
            {/* Logo */}
            <div className="text-center mb-6">
              <img
                src={Logo_Main}
                alt="Alliance Test"
                className="h-16 mx-auto"
              />
            </div>

            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800 text-center mb-2">
              {renderTitle()}
            </h1>

            {renderSubtitle() && (
              <p className="text-gray-500 text-center text-sm mb-6">
                {renderSubtitle()}
              </p>
            )}

            {!renderSubtitle() && <div className="mb-6" />}

            {/* Login Form */}
            {view === "login" && (
              <Form
                form={loginForm}
                layout="vertical"
                onFinish={onFinishLogin}
                autoComplete="off"
                className="text-left"
                size="large"
              >
                <Form.Item
                  label={
                    <span className="text-gray-700 font-medium">Email</span>
                  }
                  name="email"
                  rules={[
                    { required: true, message: "Please enter your email!" },
                    { type: "email", message: "Please enter a valid email!" },
                  ]}
                >
                  <Input
                    placeholder="Enter your Email"
                    className="h-11 rounded-lg border-gray-300 focus:border-[#004aad]"
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className="text-gray-700 font-medium">Password</span>
                  }
                  name="password"
                  rules={[
                    { required: true, message: "Please enter your password!" },
                  ]}
                >
                  <Input.Password
                    placeholder="Enter your password"
                    className="h-11 rounded-lg border-gray-300 focus:border-[#004aad]"
                  />
                </Form.Item>

                {loginError && (
                  <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-300 text-red-600 text-sm flex items-center gap-2">
                    <span>⚠</span>
                    <span>{loginError}</span>
                  </div>
                )}

                <div className="flex justify-between items-center mb-4">
                  <Form.Item name="remember" valuePropName="checked" noStyle>
                    <Checkbox className="text-gray-600 text-sm">
                      Remember me
                    </Checkbox>
                  </Form.Item>
                  <button
                    type="button"
                    onClick={() => setView("forgotEmail")}
                    className="text-[#004aad] text-sm hover:underline bg-transparent border-none cursor-pointer"
                  >
                    Forgot Password?
                  </button>
                </div>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="w-full h-11 bg-[#004aad] hover:bg-blue-800 border-none rounded-lg text-base font-medium shadow-md transition-all"
                  >
                    Login
                  </Button>
                </Form.Item>
              </Form>
            )}

            {/* Forgot Password Step 1: Enter Email */}
            {view === "forgotEmail" && (
              <Form
                form={emailForm}
                layout="vertical"
                onFinish={onSendOtp}
                autoComplete="off"
                className="text-left"
                size="large"
              >
                <Form.Item
                  label={
                    <span className="text-gray-700 font-medium">Email</span>
                  }
                  name="email"
                  rules={[
                    { required: true, message: "Please enter your email!" },
                    { type: "email", message: "Please enter a valid email!" },
                  ]}
                >
                  <Input
                    placeholder="Enter your registered email"
                    className="h-11 rounded-lg border-gray-300 focus:border-[#004aad]"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="w-full h-11 bg-[#004aad] hover:bg-blue-800 border-none rounded-lg text-base font-medium shadow-md transition-all"
                  >
                    Send OTP
                  </Button>
                </Form.Item>

                <div className="text-center mt-2">
                  <button
                    type="button"
                    onClick={goBackToLogin}
                    className="text-[#004aad] hover:underline font-medium bg-transparent border-none cursor-pointer"
                    disabled={loading}
                  >
                    Back to Login
                  </button>
                </div>
              </Form>
            )}

            {/* Forgot Password Step 2: Enter OTP */}
            {view === "forgotOtp" && (
              <Form
                form={otpForm}
                layout="vertical"
                onFinish={onVerifyOtp}
                autoComplete="off"
                className="text-left"
                size="large"
              >
                <Form.Item
                  label={
                    <span className="text-gray-700 font-medium">
                      Verification Code
                    </span>
                  }
                  name="otp"
                  rules={[
                    { required: true, message: "Please enter the OTP!" },
                    { len: 6, message: "OTP must be 6 digits!" },
                  ]}
                >
                  <Input
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="h-11 rounded-lg border-gray-300 focus:border-[#004aad] text-center text-lg tracking-widest"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="w-full h-11 bg-[#004aad] hover:bg-blue-800 border-none rounded-lg text-base font-medium shadow-md transition-all"
                  >
                    Verify OTP
                  </Button>
                </Form.Item>

                <div className="text-center mt-2 space-y-2">
                  <div>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={countdown > 0 || loading}
                      className={`font-medium bg-transparent border-none cursor-pointer ${
                        countdown > 0
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-[#004aad] hover:underline"
                      }`}
                    >
                      {countdown > 0
                        ? `Resend OTP in ${countdown}s`
                        : "Resend OTP"}
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={goBackToLogin}
                      className="text-gray-500 hover:underline font-medium bg-transparent border-none cursor-pointer text-sm"
                      disabled={loading}
                    >
                      Back to Login
                    </button>
                  </div>
                </div>
              </Form>
            )}

            {/* Forgot Password Step 3: New Password */}
            {view === "forgotReset" && (
              <Form
                form={resetForm}
                layout="vertical"
                onFinish={onResetPassword}
                autoComplete="off"
                className="text-left"
                size="large"
              >
                <Form.Item
                  label={
                    <span className="text-gray-700 font-medium">
                      New Password
                    </span>
                  }
                  name="newPassword"
                  rules={[
                    { required: true, message: "Please enter a new password!" },
                    {
                      min: 6,
                      message: "Password must be at least 6 characters!",
                    },
                    {
                      pattern: /^(?=.*[A-Za-z])(?=.*\d)/,
                      message:
                        "Password must contain at least one letter and one number!",
                    },
                  ]}
                >
                  <Input.Password
                    placeholder="Enter new password"
                    className="h-11 rounded-lg border-gray-300 focus:border-[#004aad]"
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className="text-gray-700 font-medium">
                      Confirm Password
                    </span>
                  }
                  name="confirmPassword"
                  dependencies={["newPassword"]}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your password!",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match!")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="Confirm new password"
                    className="h-11 rounded-lg border-gray-300 focus:border-[#004aad]"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="w-full h-11 bg-[#004aad] hover:bg-blue-800 border-none rounded-lg text-base font-medium shadow-md transition-all"
                  >
                    Reset Password
                  </Button>
                </Form.Item>

                <div className="text-center mt-2">
                  <button
                    type="button"
                    onClick={goBackToLogin}
                    className="text-gray-500 hover:underline font-medium bg-transparent border-none cursor-pointer text-sm"
                    disabled={loading}
                  >
                    Back to Login
                  </button>
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
